import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { authActions } from 'features/Auth/authSlice';

import { RootState } from '../../store';

import { GetActivitiesResponse } from './types';

export interface APIResponse<T> {
  data: T;
}

export const getBackendUrl = () => {
  let url = process.env.REACT_APP_BASE_URL
    ? process.env.REACT_APP_BASE_URL
    : `${window.location.protocol}//be.${window.location.host}`;
  if (process.env.REACT_APP_ORGANISATION === 'LEASEIQ' && !url.endsWith('/api')) {
    // For some reasons LeaseIQ project, URL is expected to be suffixed with /api while Busmin is not
    url += '/api';
  }

  return url;
};

const getUserTokenFromStorage = () => {
  if (sessionStorage.getItem('user') || localStorage.getItem('user')) {
    const userDetail = JSON.parse(sessionStorage.getItem('user') || localStorage.getItem('user') || '');

    return userDetail?.token;
  }

  return null;
};

const rtkBaseQuery = fetchBaseQuery({
  baseUrl: getBackendUrl(),
  // prepareHeaders is used to configure the header of every request and gives access to getState which we use to include the token from the store
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.token || getUserTokenFromStorage();

    if (token) {
      // include token in req header
      headers.set('Authorization', `Token ${token}`);
      return headers;
    }
  },
});

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  let result = await rtkBaseQuery(args, api, extraOptions);
  if (result.error && result.error.status === 401) {
    // try to get a new token
    // const refreshResult = await rtkBaseQuery({ url: '/refresh-token/', method: 'POST' }, api, extraOptions);
    const refreshResult = await rtkBaseQuery('/refresh-token/', api, extraOptions);
    if (refreshResult.data) {
      // TODO - Need to implement refresh token
      // api.dispatch(authActions.setCredentials(refreshResult));
      result = await rtkBaseQuery(args, api, extraOptions);
    } else {
      api.dispatch(authActions.resetCredentials());
    }
  }
  return result;
};

export const BUSMAN_TAGS = {
  Worklog: 'Worklog',
  Client: 'Client',
  Productivity: 'Productivity',
  Job: 'Job',
  User: 'User',
  Category: 'Category',
  Team: 'Team',
  JobComments: 'JobComments',
  LogComments: 'LogComments',
  FCM: 'FCM',
  Notification: 'Notification',
  JobWorklog: 'JobWorklog',
  Quote: 'Quote',
  QuoteById: 'QuoteById',
  WorklogCount: 'WorklogCount',
  Insights: 'Insights',
  Attachment: 'Attachment',
  Projects: 'Projects',
  ProjectScopes: 'ProjectScopes',
  TaxCode: 'TaxCode',
  // LeaseIQ Tags
  Lease: 'Lease',
  Lessee: 'Lessee',
  LeaseIQUsers: 'LeaseIQUsers',
  LeaseComments: 'LeaseComments',
  UserGroups: 'UserGroups',
} as const;

export type BusmanTagType = (typeof BUSMAN_TAGS)[keyof typeof BUSMAN_TAGS];

// Define a service using a base URL and expected endpoints
export const busmanApiSlice = createApi({
  reducerPath: 'busmanApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: Object.values(BUSMAN_TAGS),

  endpoints: (builder) => ({
    login: builder.mutation({
      query: (credentials) => ({
        url: '/login/',
        method: 'post',
        body: credentials,
      }),
    }),

    // Fetches all activities
    getActivities: builder.query({
      query: ({ isActive = 'true' }) => `/activities/?is_active=${isActive}`,
      transformResponse: (response: GetActivitiesResponse) => response.data,
    }),

    // Change Password
    changePassword: builder.mutation({
      query: (data) => {
        return {
          url: 'reset-password/',
          method: 'post',
          body: data,
        };
      },
    }),

    //Request reset password link
    requestResetPassword: builder.mutation({
      query: (data) => {
        return {
          url: '/request-reset-password/',
          method: 'post',
          body: data,
        };
      },
    }),

    //Reset User Password
    resetPassword: builder.mutation({
      query: ({ userId, token, newPass }) => {
        return {
          url: `password-reset-complete/${userId}/${token}/`,
          method: 'post',
          body: newPass,
        };
      },
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  useGetActivitiesQuery,
  useLoginMutation,
  useChangePasswordMutation,
  useRequestResetPasswordMutation,
  useResetPasswordMutation,
} = busmanApiSlice;
